import React, { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase-auth';
import { useAuth } from '../../../contexts/AuthContextLazy';
import { showSuccess, showError } from '../../../utils/toast';
import {
  ChatBubbleLeftRightIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ClipboardDocumentListIcon,
  SparklesIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  CalendarIcon,
  DocumentTextIcon,
  InboxIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

interface TemplateCategory {
  id: string;
  name: string;
  description: string;
  icon: string;
  sort_order: number;
  is_active: boolean;
}

interface MessageTemplate {
  id: string;
  category_id: string | null;
  name: string;
  description: string;
  content: string;
  variables: string[];
  usage_count: number;
  last_used_at: string | null;
  created_by: string | null;
  is_global: boolean;
  is_active: boolean;
  created_at: string;
  category?: TemplateCategory;
}

interface QuickReply {
  id: string;
  label: string;
  content: string;
  category: string | null;
  usage_count: number;
  created_by: string | null;
  is_global: boolean;
  is_active: boolean;
  sort_order: number;
}

const iconComponents: { [key: string]: any } = {
  'CalendarIcon': CalendarIcon,
  'DocumentTextIcon': DocumentTextIcon,
  'ChatBubbleLeftRightIcon': ChatBubbleLeftRightIcon,
  'SparklesIcon': SparklesIcon,
  'ChartBarIcon': ChartBarIcon,
  'InboxIcon': InboxIcon
};

export const SMSTemplatesAdmin: React.FC = () => {
  const { hasRole, user } = useAuth();
  const [activeTab, setActiveTab] = useState<'templates' | 'quick-replies'>('templates');
  const [categories, setCategories] = useState<TemplateCategory[]>([]);
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [quickReplies, setQuickReplies] = useState<QuickReply[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showQuickReplyModal, setShowQuickReplyModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<MessageTemplate | null>(null);
  const [editingQuickReply, setEditingQuickReply] = useState<QuickReply | null>(null);

  useEffect(() => {
    if (hasRole(['super_admin', 'pcm_staff'])) {
      fetchData();
    }
  }, [hasRole]);

  const fetchData = async () => {
    try {
      // Fetch categories
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('sms_template_categories')
        .select('*')
        .order('sort_order');

      if (categoriesError) throw categoriesError;
      setCategories(categoriesData || []);

      // Fetch templates with categories
      const { data: templatesData, error: templatesError } = await supabase
        .from('sms_message_templates')
        .select(`
          *,
          category:sms_template_categories(*)
        `)
        .order('name');

      if (templatesError) throw templatesError;
      setTemplates(templatesData || []);

      // Fetch quick replies
      const { data: quickRepliesData, error: quickRepliesError } = await supabase
        .from('sms_quick_replies')
        .select('*')
        .order('sort_order');

      if (quickRepliesError) throw quickRepliesError;
      setQuickReplies(quickRepliesData || []);
    } catch (error) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Error fetching data:', error);
        }

      }
      showError('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.content.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || template.category_id === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const filteredQuickReplies = quickReplies.filter(reply => {
    return reply.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
           reply.content.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleDeleteTemplate = async (id: string) => {
    if (!confirm('Are you sure you want to delete this template?')) return;

    try {
      const { error } = await supabase
        .from('sms_message_templates')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setTemplates(templates.filter(t => t.id !== id));
      showSuccess('Template deleted successfully');
    } catch (error) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Error deleting template:', error);
        }

      }
      showError('Failed to delete template');
    }
  };

  const handleDeleteQuickReply = async (id: string) => {
    if (!confirm('Are you sure you want to delete this quick reply?')) return;

    try {
      const { error } = await supabase
        .from('sms_quick_replies')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setQuickReplies(quickReplies.filter(r => r.id !== id));
      showSuccess('Quick reply deleted successfully');
    } catch (error) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Error deleting quick reply:', error);
        }

      }
      showError('Failed to delete quick reply');
    }
  };

  const renderIcon = (iconName: string) => {
    const IconComponent = iconComponents[iconName] || InboxIcon;
    return <IconComponent className="h-5 w-5" />;
  };

  if (!hasRole(['super_admin', 'pcm_staff'])) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">You don't have permission to view this page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('templates')}
            className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'templates'
                ? 'border-amber-500 text-amber-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Message Templates
            <span className="ml-2 text-xs text-gray-400">({templates.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('quick-replies')}
            className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'quick-replies'
                ? 'border-amber-500 text-amber-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Quick Replies
            <span className="ml-2 text-xs text-gray-400">({quickReplies.length})</span>
          </button>
        </nav>
      </div>

      {/* Search and Filters */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500 w-64"
            />
          </div>
          
          {activeTab === 'templates' && (
            <select
              value={selectedCategory || ''}
              onChange={(e) => setSelectedCategory(e.target.value || null)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500"
            >
              <option value="">All Categories</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          )}
        </div>

        <button
          onClick={() => {
            if (activeTab === 'templates') {
              setEditingTemplate(null);
              setShowTemplateModal(true);
            } else {
              setEditingQuickReply(null);
              setShowQuickReplyModal(true);
            }
          }}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          {activeTab === 'templates' ? 'New Template' : 'New Quick Reply'}
        </button>
      </div>

      {/* Content */}
      {activeTab === 'templates' ? (
        <div className="bg-white portal-content-shadow overflow-hidden sm:rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Template
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Variables
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Usage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredTemplates.map((template) => (
                <tr key={template.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{template.name}</div>
                      <div className="text-sm text-gray-500 truncate max-w-md">
                        {template.content}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {template.category && (
                      <div className="flex items-center">
                        {renderIcon(template.category.icon)}
                        <span className="ml-2 text-sm text-gray-900">{template.category.name}</span>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {template.variables.map((variable, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800"
                        >
                          {`{{${variable}}}`}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{template.usage_count} uses</div>
                    {template.last_used_at && (
                      <div className="text-xs text-gray-500">
                        Last: {new Date(template.last_used_at).toLocaleDateString()}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      template.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {template.is_active ? 'Active' : 'Inactive'}
                    </span>
                    {template.is_global && (
                      <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Global
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => {
                        setEditingTemplate(template);
                        setShowTemplateModal(true);
                      }}
                      className="text-amber-600 hover:text-amber-900 mr-3"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDeleteTemplate(template.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredQuickReplies.map((reply) => (
            <div key={reply.id} className="bg-white portal-content-shadow rounded-lg shadow p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-900">{reply.label}</h3>
                  <p className="mt-1 text-sm text-gray-500">{reply.content}</p>
                  <div className="mt-2 flex items-center space-x-2">
                    {reply.category && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                        {reply.category}
                      </span>
                    )}
                    <span className="text-xs text-gray-500">{reply.usage_count} uses</span>
                  </div>
                </div>
                <div className="ml-4 flex items-center space-x-2">
                  <button
                    onClick={() => {
                      setEditingQuickReply(reply);
                      setShowQuickReplyModal(true);
                    }}
                    className="text-amber-600 hover:text-amber-900"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteQuickReply(reply.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Template Modal */}
      {showTemplateModal && (
        <TemplateModal
          template={editingTemplate}
          categories={categories}
          onClose={() => {
            setShowTemplateModal(false);
            setEditingTemplate(null);
          }}
          onSave={() => {
            setShowTemplateModal(false);
            setEditingTemplate(null);
            fetchData();
          }}
        />
      )}

      {/* Quick Reply Modal */}
      {showQuickReplyModal && (
        <QuickReplyModal
          quickReply={editingQuickReply}
          onClose={() => {
            setShowQuickReplyModal(false);
            setEditingQuickReply(null);
          }}
          onSave={() => {
            setShowQuickReplyModal(false);
            setEditingQuickReply(null);
            fetchData();
          }}
        />
      )}
    </div>
  );
};

// Template Modal Component
const TemplateModal: React.FC<{
  template: MessageTemplate | null;
  categories: TemplateCategory[];
  onClose: () => void;
  onSave: () => void;
}> = ({ template, categories, onClose, onSave }) => {
  const { user } = useAuth();
  const [name, setName] = useState(template?.name || '');
  const [description, setDescription] = useState(template?.description || '');
  const [content, setContent] = useState(template?.content || '');
  const [categoryId, setCategoryId] = useState(template?.category_id || '');
  const [isGlobal, setIsGlobal] = useState(template?.is_global ?? true);
  const [isActive, setIsActive] = useState(template?.is_active ?? true);
  const [variables, setVariables] = useState<string[]>(template?.variables || []);
  const [saving, setSaving] = useState(false);

  // Extract variables from content
  useEffect(() => {
    const matches = content.match(/\{\{([^}]+)\}\}/g);
    if (matches) {
      const vars = matches.map(match => match.replace(/[{}]/g, '').trim());
      setVariables([...new Set(vars)]);
    } else {
      setVariables([]);
    }
  }, [content]);

  const handleSave = async () => {
    if (!name.trim() || !content.trim()) {
      showError('Name and content are required');
      return;
    }

    setSaving(true);
    
    try {
      // Get current user's platform user ID
      const { data: platformUser } = await supabase
        .from('platform_users')
        .select('id')
        .eq('auth_user_id', user?.id)
        .single();

      if (!platformUser) {
        throw new Error('Could not find platform user');
      }

      const templateData = {
        name,
        description,
        content,
        category_id: categoryId || null,
        is_global: isGlobal,
        is_active: isActive,
        variables,
        created_by: template?.created_by || platformUser.id
      };

      if (template) {
        // Update existing template
        const { error } = await supabase
          .from('sms_message_templates')
          .update(templateData)
          .eq('id', template.id);

        if (error) throw error;
      } else {
        // Create new template
        const { error } = await supabase
          .from('sms_message_templates')
          .insert(templateData);

        if (error) throw error;
      }

      showSuccess(template ? 'Template updated successfully' : 'Template created successfully');
      onSave();
    } catch (error) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Error saving template:', error);
        }

      }
      showError('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white portal-content-shadow rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {template ? 'Edit Template' : 'New Template'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Template Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500"
              placeholder="e.g., Appointment Reminder"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500"
              placeholder="Brief description of when to use this template"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500"
            >
              <option value="">No Category</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Message Content
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500"
              placeholder="Hi {{client_name}}, this is a reminder about..."
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              Use {{variable_name}} for dynamic content. Common variables: client_name, advisor_name, meeting_date, etc.
            </p>
          </div>

          {variables.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Detected Variables
              </label>
              <div className="flex flex-wrap gap-1">
                {variables.map((variable, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800"
                  >
                    {`{{${variable}}}`}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={isGlobal}
                onChange={(e) => setIsGlobal(e.target.checked)}
                className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Available to all advisors</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Active</span>
            </label>
          </div>
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Template'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Quick Reply Modal Component
const QuickReplyModal: React.FC<{
  quickReply: QuickReply | null;
  onClose: () => void;
  onSave: () => void;
}> = ({ quickReply, onClose, onSave }) => {
  const { user } = useAuth();
  const [label, setLabel] = useState(quickReply?.label || '');
  const [content, setContent] = useState(quickReply?.content || '');
  const [category, setCategory] = useState(quickReply?.category || '');
  const [isGlobal, setIsGlobal] = useState(quickReply?.is_global ?? true);
  const [isActive, setIsActive] = useState(quickReply?.is_active ?? true);
  const [sortOrder, setSortOrder] = useState(quickReply?.sort_order || 0);
  const [saving, setSaving] = useState(false);

  const categories = ['greeting', 'acknowledgment', 'scheduling', 'document', 'info', 'other'];

  const handleSave = async () => {
    if (!label.trim() || !content.trim()) {
      showError('Label and content are required');
      return;
    }

    setSaving(true);
    
    try {
      // Get current user's platform user ID
      const { data: platformUser } = await supabase
        .from('platform_users')
        .select('id')
        .eq('auth_user_id', user?.id)
        .single();

      if (!platformUser) {
        throw new Error('Could not find platform user');
      }

      const replyData = {
        label,
        content,
        category: category || null,
        is_global: isGlobal,
        is_active: isActive,
        sort_order: sortOrder,
        created_by: quickReply?.created_by || platformUser.id
      };

      if (quickReply) {
        // Update existing quick reply
        const { error } = await supabase
          .from('sms_quick_replies')
          .update(replyData)
          .eq('id', quickReply.id);

        if (error) throw error;
      } else {
        // Create new quick reply
        const { error } = await supabase
          .from('sms_quick_replies')
          .insert(replyData);

        if (error) throw error;
      }

      showSuccess(quickReply ? 'Quick reply updated successfully' : 'Quick reply created successfully');
      onSave();
    } catch (error) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Error saving quick reply:', error);
        }

      }
      showError('Failed to save quick reply');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white portal-content-shadow rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {quickReply ? 'Edit Quick Reply' : 'New Quick Reply'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Button Label
            </label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500"
              placeholder="e.g., Thank You"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Message Content
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500"
              placeholder="Thank you for your message. I'll get back to you shortly."
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500"
            >
              <option value="">No Category</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>
                  {cat.charAt(0).toUpperCase() + cat.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sort Order
            </label>
            <input
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(parseInt(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500"
              placeholder="0"
            />
          </div>

          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={isGlobal}
                onChange={(e) => setIsGlobal(e.target.checked)}
                className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Available to all advisors</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Active</span>
            </label>
          </div>
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Quick Reply'}
          </button>
        </div>
      </div>
    </div>
  );
};